import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { ToolForm, ToolRunStatus, EvidencePackDownloads } from './ToolComponents';
import { ToolGate } from '../../components/ToolGate';
import { RepoPicker } from '../../components/RepoPicker';
import { GuestToolPanel } from '../../components/GuestToolPanel';
import { UpgradeBanner } from '../../components/UpgradeBanner';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';
import { ToolConversionHeader } from '../../components/ToolConversionHeader';
import { toolsSeoIndexingEnabled } from '../../config/seo';
import { useToolReadiness } from '../../hooks/useToolReadiness';
import MarketingLayout from '../../components/MarketingLayout';
import Layout from '../../components/Layout';
import { ToolReadinessError } from '../../components/ToolReadinessError';
import { appLinks } from '../../lib/crossSiteLinks';
import { planLabels, resolveBannerMode } from '../../lib/billingUi';
import { getHubUrl } from '../../lib/apiBase';
import { prApprovalsApi, downloadApi } from '../../lib/toolsApi';
import { useAuthStore } from '../../stores/authStore';
import api from '../../lib/api';
import SEO from '../../components/SEO';

/**
 * PR Approvals Report Tool Page
 * Generates evidence packs showing PR approval history for audit/compliance purposes
 */
export const PrApprovalsPage: React.FC = () => {
  const readiness = useToolReadiness('pr-approvals');
  const authHydrated = useAuthStore((state) => state._hasHydrated);
  const readinessAuth = readiness.data?.isAuthenticated;
  const storeAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthenticated = readinessAuth ?? (authHydrated ? storeAuthenticated : undefined);
  const isGuest = isAuthenticated === false;
  const tenantContext = useAuthStore((state) => state.tenantContext);
  const canFetchBillingStatus = isAuthenticated === true
    && (tenantContext?.role === 'Owner' || tenantContext?.role === 'Admin');
  const [status, setStatus] = useState<'idle' | 'generating' | 'completed' | 'error'>('idle');
  const [packId, setPackId] = useState<string>('');
  const [checksum, setChecksum] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [progressMessage, setProgressMessage] = useState<string>('');
  const [signalRReady, setSignalRReady] = useState<boolean>(false);
  const pollingIntervalRef = useRef<number | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);

  const { data: billingStatus } = useQuery({
    queryKey: ['billing', 'status', tenantContext?.tenantId],
    enabled: canFetchBillingStatus && !!tenantContext?.tenantId,
    queryFn: async () => {
      const response = await api.get('/v1/billing/status');
      return response.data as { planCode: string; planDisplayName: string; canShareEvidencePacks?: boolean };
    }
  });

  const bannerMode = resolveBannerMode(isAuthenticated === true, billingStatus?.planCode, billingStatus?.planDisplayName);
  const { packLimitLabel, retentionLabel } = planLabels(billingStatus?.planCode);

  const breadcrumbItems = [
    { name: 'Home', url: 'https://evidenttrail.com/' },
    { name: 'Tools', url: 'https://evidenttrail.com/tools' },
    { name: 'PR Approvals Report', url: 'https://evidenttrail.com/tools/pr-approvals-report' }
  ];

  const faqs = [
    {
      question: 'Does the report include code changes?',
      answer: 'No. The report is metadata-only and does not include source code content or diffs.'
    },
    {
      question: 'Can I run this without creating an account?',
      answer: 'Yes. Guest mode allows one report for one repository with 24-hour retention.'
    },
    {
      question: 'What approval data is included?',
      answer: 'Reviewer identity, approval timestamps, and required approval thresholds are included.'
    },
    {
      question: 'Does this certify compliance?',
      answer: 'No. It provides evidence packs that support audit reviews; it does not certify compliance.'
    }
  ];

  const longformSections = [
    {
      title: 'What this free report includes',
      paragraphs: [
        'The PR approvals report summarizes who reviewed a change, when approvals happened, and which required checks passed. It is built from GitHub metadata and exported as a tamper-evident pack.'
      ],
      bullets: [
        'Pull request IDs, titles, and merge timestamps',
        'Reviewer identities and approval timestamps',
        'Required approval threshold evaluation',
        'CI check status metadata and merge commit IDs',
        'SHA-256 checksums with a manifest file'
      ]
    },
    {
      title: 'What auditors ask for',
      paragraphs: [
        'SOC 2 and ISO 27001 change control reviews typically request evidence that approvals happened before merge and that required checks were enforced. The report provides a reproducible export for those samples.'
      ],
      bullets: [
        'Approval evidence tied to each merged PR',
        'Required checks status and timestamps',
        'A single export that can be verified later'
      ]
    },
    {
      title: 'How EvidentTrail works (read-only)',
      paragraphs: [
        'EvidentTrail connects with read-only GitHub App access. It captures metadata only and never writes to repositories.'
      ],
      bullets: [
        'Connect the read-only GitHub App',
        'Select one repository in guest mode',
        'Generate PDF, CSV, JSON, and manifest exports'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Use the approval table to confirm reviewers and timing. Use the required checks section to show that CI gates were satisfied for each merge.'
      ],
      bullets: [
        'Match approval timestamps to merge times',
        'Confirm required checks are marked as passed',
        'Share the manifest checksum with auditors'
      ]
    },
    {
      title: 'Boundaries (what we do not collect)',
      paragraphs: [
        'Evidence packs are metadata-only. We do not store source code content, review comments, prompts, or build logs.'
      ],
      bullets: [
        'No source code file contents or diffs',
        'No pull request descriptions or review comments',
        'No build logs, prompts, or secrets'
      ]
    },
    {
      title: 'When to upgrade',
      paragraphs: [
        'Guest mode generates a one-off pack. Upgrading unlocks scheduling, retention, and org-wide coverage.'
      ],
      bullets: [
        'Automate monthly evidence packs',
        'Retain evidence beyond the guest window',
        'Export org-wide evidence packs across repos'
      ]
    }
  ];

  // Repository selection state
  const [repoSelection, setRepoSelection] = useState<{
    installationId: string;
    organization: string;
    repositories: string[];
  } | null>(null);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Poll evidence pack status
  const pollPackStatus = async (id: string) => {
    try {
      const response = await api.get(`/v1/tools/evidence-packs/${id}`);
      const pack = response.data;

      if (pack.status === 'Ready') {
        setChecksum(pack.checksum || '');
        setStatus('completed');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (pack.status === 'Failed') {
        setError(pack.errorMessage || 'Generation failed');
        setStatus('error');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error polling pack status:', err);
    }
  };

  // Start polling when packId changes
  useEffect(() => {
    if (packId && status === 'generating' && !signalRReady) {
      pollingIntervalRef.current = window.setInterval(() => {
        pollPackStatus(packId);
      }, 2000); // Poll every 2 seconds (fallback)
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [packId, status, signalRReady]);

  // SignalR subscription for real-time updates
  useEffect(() => {
    if (!packId || status !== 'generating') {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(getHubUrl('/hubs/evidence'))
      .withAutomaticReconnect()
      .build();

    connection.on('PackProgress', (payload: any) => {
      setProgressMessage(payload.step || 'Generating...');
    });

    connection.on('PackCompleted', (payload: any) => {
      setChecksum(payload.checksum || '');
      setStatus('completed');
      setProgressMessage('');
      stopPolling();
    });

    connection.on('PackFailed', (payload: any) => {
      setError(payload.errorMessage || 'Generation failed');
      setStatus('error');
      setProgressMessage('');
      stopPolling();
    });

    connection
      .start()
      .then(() => connection.invoke('SubscribeToPack', packId))
      .then(() => setSignalRReady(true))
      .catch((err: unknown) => {
        console.warn('SignalR connection failed, falling back to polling', err);
        setSignalRReady(false);
      });

    connectionRef.current = connection;

    return () => {
      setSignalRReady(false);
      connection.stop().catch(() => undefined);
      stopPolling();
    };
  }, [packId, status]);

  const handleSubmit = async (data: any) => {
    if (!repoSelection || repoSelection.repositories.length === 0) {
      setStatus('error');
      setError('Please select at least one repository');
      return;
    }

    setStatus('generating');
    setError('');

    try {
      const result = await prApprovalsApi.generate({
        installationId: repoSelection.installationId,
        organization: repoSelection.organization,
        repositories: repoSelection.repositories,
        baseBranch: data.baseBranch || 'main',
        startDate: data.startDate ? new Date(data.startDate).toISOString() : null,
        endDate: data.endDate ? new Date(data.endDate).toISOString() : null,
        authorFilter: data.authorFilter || null,
        requiredApprovalsCount: parseInt(data.requiredApprovalsCount || '1', 10)
      });

      setPackId(result.packId);
      // Status remains 'generating' and polling will update it
    } catch (err: any) {
      setStatus('error');
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
    }
  };

  const toolUI = (
    <div className={isAuthenticated ? 'py-8' : 'max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8 py-12'}>
      {isGuest && (
        <div className="mb-8 rounded-2xl border border-slate-700/60 bg-slate-800/60 p-6 text-slate-200">
          <h3 className="text-lg font-semibold text-white mb-3">Guest mode quick start</h3>
          <ol className="space-y-2 text-sm text-slate-300">
            <li>1. Connect the read-only GitHub App.</li>
            <li>2. Select one repository for this session.</li>
            <li>3. Generate a single PR approvals evidence pack.</li>
          </ol>
          <p className="text-xs text-slate-400 mt-4">
            Guest sessions allow one report per repository with 24-hour retention.
          </p>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <div id="report-form" className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Generate Report</h2>

            {readiness.error ? (
              <ToolReadinessError toolName="PR Approvals Tool" onRetry={() => readiness.refetch()} />
            ) : isGuest ? (
              <GuestToolPanel
                toolKey="pr-approvals"
                toolLabel="PR approvals"
                description="Generate a PR approvals evidence pack before creating an account."
              />
            ) : (
              <>
                {/* Repository Picker */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Select Repositories *
                  </label>
                  <RepoPicker
                    value={repoSelection}
                    onChange={setRepoSelection}
                    multiSelect={true}
                  />
                </div>

                <ToolForm onSubmit={handleSubmit} isLoading={status === 'generating'}>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Base Branch
                      </label>
                      <input
                        type="text"
                        name="baseBranch"
                        placeholder="main"
                        className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min Approvals
                      </label>
                      <input
                        type="number"
                        name="requiredApprovalsCount"
                        defaultValue="1"
                        min="1"
                        max="10"
                        className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Filter by Author (optional)
                    </label>
                    <input
                      type="text"
                      name="authorFilter"
                      placeholder="GitHub username"
                      className="w-full px-4 py-2 border border-slate-700 rounded-lg bg-slate-900 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </ToolForm>

                <ToolRunStatus
                  status={status}
                  message={status === 'generating' ? (progressMessage || 'Fetching PR data and generating reports...') : undefined}
                  error={error}
                />

                {status === 'completed' && packId && (
                  <>
                    <EvidencePackDownloads
                      packId={packId}
                      checksum={checksum}
                      formats={isGuest ? ['pdf'] : ['pdf', 'csv', 'json', 'manifest']}
                      onDownload={(format: 'pdf' | 'csv' | 'json' | 'manifest') => {
                        window.location.href = downloadApi.getDownloadUrl(packId, format);
                      }}
                      showShareButton={billingStatus?.canShareEvidencePacks ?? true}
                    />
                    <div className="mt-6">
                      <UpgradeBanner
                        mode={bannerMode}
                        planCode={billingStatus?.planCode}
                        planDisplayName={billingStatus?.planDisplayName}
                        packLimitLabel={packLimitLabel}
                        retentionLabel={retentionLabel}
                        variant="primary"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-3">What You Get</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✅ Detailed PR approval history</li>
              <li>✅ Reviewer information & timestamps</li>
              <li>✅ Approval threshold evaluation</li>
              <li>✅ SHA-256 integrity checksums</li>
              <li>✅ PDF, CSV, JSON, and manifest exports</li>
            </ul>
          </div>

          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <h3 className="font-semibold text-gray-900 mb-3">Perfect For</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>📋 SOC 2 change management evidence</li>
              <li>🔒 ISO 27001 change control reviews</li>
              <li>⚖️ Internal audit sampling</li>
              <li>📊 Evidence pack exports for auditors</li>
            </ul>
          </div>
        </div>
      </div>

      {isAuthenticated === false && (
        <>
          {/* CTA Section */}
          <div className="mt-12 rounded-2xl border border-slate-700/60 bg-slate-800/60 p-6 text-slate-200 shadow-xl">
            <h3 className="text-xl font-bold text-white mb-3">
              Need continuous evidence history?
            </h3>
            <p className="text-slate-300 mb-4">
              The core platform keeps audit timelines, drift changes, exception records, and evidence pack retention in one place.
            </p>
            <a
              href={appLinks.register}
              className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-500 transition font-semibold"
            >
              Explore the Core Platform →
            </a>
          </div>

          <div className="mt-10 grid md:grid-cols-6 gap-6">
            <Link to="/github-change-control" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">GitHub Change Control</h3>
              <p className="text-sm text-slate-300">See how approval evidence maps to change control requirements.</p>
            </Link>
            <Link to="/soc2-change-management-evidence" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">SOC 2 Evidence</h3>
              <p className="text-sm text-slate-300">Use approval exports for CC8.1 and CC6.1 sampling.</p>
            </Link>
            <Link to="/tools" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">All Evidence Tools</h3>
              <p className="text-sm text-slate-300">Compare the PR approvals and branch protection tools.</p>
            </Link>
            <Link to="/solutions" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">Solutions</h3>
              <p className="text-sm text-slate-300">Explore compliance-focused evidence workflows.</p>
            </Link>
            <Link to="/security" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">Security</h3>
              <p className="text-sm text-slate-300">Review read-only access and data minimization.</p>
            </Link>
            <Link to="/faq" className="rounded-2xl border border-slate-700/60 bg-slate-800/60 p-5 text-slate-200 hover:border-blue-500/50 transition-colors">
              <h3 className="text-white font-semibold mb-2">FAQ</h3>
              <p className="text-sm text-slate-300">Common questions about evidence packs.</p>
            </Link>
          </div>
        </>
      )}
    </div>
  );

  const toolContent = isGuest || readiness.error || readiness.isLoading ? (
    toolUI
  ) : (
    <ToolGate toolKey="pr-approvals" gateName="PR Approvals Evidence">
      {toolUI}
    </ToolGate>
  );

  const guestPageContent = (
    <>
      {!toolsSeoIndexingEnabled && (
        <Helmet>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      )}
      <BreadcrumbJsonLd items={breadcrumbItems} />
      <FaqJsonLd items={faqs} />

      <ToolConversionHeader
        title="Generate a PR approvals evidence pack"
        description="Use this report to show reviewers, approvals, and required checks for a sampled set of pull requests. The export is metadata-only and does not include source code."
        primaryHref="#report-form"
        secondaryHref="/sample-timeline"
        secondaryLabel="See sample output"
      />

      <div className="max-w-3xl mx-auto mb-8 text-center">
        <Link to="/sample-timeline" className="text-primary-400 hover:underline">
          View the full sample audit timeline output {"->"}
        </Link>
      </div>

      {toolContent}

      <SeoLongformSections sections={longformSections} faqs={faqs} />
    </>
  );

  if (isAuthenticated === undefined) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <Layout skipTenantRedirect>
        <SEO
          title="PR Approval Evidence Exporter | EvidentTrail"
          description="Generate PR approval evidence packs with PDF, CSV, JSON, and manifest exports plus SHA-256 checksums."
        />
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-white mb-2">PR Approval Evidence Exporter</h1>
          <p className="text-slate-300">
            Generate evidence packs of pull request approvals with tamper-evident checksums and multiple export formats.
          </p>
        </div>
        {toolContent}
      </Layout>
    );
  }

  return (
    <MarketingLayout
      title="PR Approval Evidence Exporter | EvidentTrail"
      description="Generate PR approval evidence packs with PDF, CSV, JSON, and manifest exports plus SHA-256 checksums."
      h1="PR Approval Evidence Exporter"
      heroText="Generate evidence packs of pull request approvals with tamper-evident checksums and multiple export formats."
      type="website"
    >
      {guestPageContent}
    </MarketingLayout>
  );
};

export default PrApprovalsPage;
